import { QueryClient } from '@tanstack/react-query'

/**
 * Cache duration tiers for different content types
 * Optimized for high load scenarios
 */
export const CACHE_TIMES = {
  // Static content that rarely changes (tours, activities, site settings)
  STATIC: {
    staleTime: 30 * 60 * 1000,  // 30 minutes - data considered fresh
    gcTime: 60 * 60 * 1000,     // 60 minutes - keep in cache
  },
  // Semi-static content (blog posts, categories)
  SEMI_STATIC: {
    staleTime: 15 * 60 * 1000,  // 15 minutes
    gcTime: 30 * 60 * 1000,     // 30 minutes
  },
  // Dynamic content (bookings, schedules, availability)
  DYNAMIC: {
    staleTime: 2 * 60 * 1000,   // 2 minutes
    gcTime: 10 * 60 * 1000,     // 10 minutes
  },
  // Real-time content (user sessions, live data)
  REALTIME: {
    staleTime: 30 * 1000,       // 30 seconds
    gcTime: 5 * 60 * 1000,      // 5 minutes
  },
} as const

/**
 * Check if error is caused by browser extension blocking request
 */
function isBlockedByClient(error: unknown): boolean {
  const errorMessage = (error as Error)?.message || String(error)
  return (
    errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
    errorMessage.includes('net::ERR_BLOCKED_BY_CLIENT') ||
    ((error as Error)?.name === 'NetworkError' && navigator.onLine !== false)
  )
}

/**
 * Check if we should retry based on error type
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  // Don't retry if blocked by browser extension (ad blocker)
  if (isBlockedByClient(error)) {
    console.warn(
      '[Network] Request blocked by browser extension (likely ad blocker).',
      'Please disable ad blockers for localhost:54321 to ensure proper functionality.'
    )
    return false
  }

  // Don't retry on 404s or other client errors
  const errorStatus = (error as { status?: number; response?: { status?: number } })?.status
    || (error as { status?: number; response?: { status?: number } })?.response?.status
  if (errorStatus && errorStatus >= 400 && errorStatus < 500) {
    return false
  }

  // Retry up to 2 times for server errors
  return failureCount < 2
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default to semi-static for most content
      staleTime: CACHE_TIMES.SEMI_STATIC.staleTime,
      // @ts-expect-error - gcTime is the v5 name for cacheTime, types may not be updated
      gcTime: CACHE_TIMES.SEMI_STATIC.gcTime,

      // Retry logic
      retry: shouldRetry,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Network optimization
      refetchOnWindowFocus: false,    // Prevent unnecessary refetches on tab switch
      refetchOnReconnect: 'always',   // Refetch when internet reconnects
      refetchOnMount: false,          // Don't refetch if data is fresh

      // Structural sharing for better performance with large objects
      structuralSharing: true,

      // Network mode: always try cache first, then network
      networkMode: 'offlineFirst',
    },
    mutations: {
      retry: false,
      networkMode: 'online',
    },
  },
})
